import type { AmountUnit, PolicyGroup, PolicyKey, PolicyScope, Weekday } from "@mubin/contracts";

export const policyGroupLabel: Record<PolicyGroup, string> = {
  memorization: "الحفظ وأيام الدراسة",
  review: "المراجعة",
  taahud: "خاص ببرنامج التعاهد",
  grading: "الدرجات وإعادة التسميع",
  absence: "الغياب",
  struggling: "الطالب المتعثر",
  results: "تعديل النتائج",
};

export const policyScopeLabel: Record<PolicyScope, string> = {
  organization: "الجمعية",
  program: "البرنامج",
  branch: "المجمع",
  circle: "الحلقة",
};

export const unitLabel: Record<AmountUnit, string> = {
  ayah: "آية",
  page: "وجه (صفحة)",
  rub: "ربع حزب",
  juz: "جزء",
  surah: "سورة",
};

export const weekdayLabel: Record<Weekday, string> = {
  sat: "السبت",
  sun: "الأحد",
  mon: "الاثنين",
  tue: "الثلاثاء",
  wed: "الأربعاء",
  thu: "الخميس",
  fri: "الجمعة",
};

export type PolicyInput =
  | { type: "select"; options: Record<string, string> }
  | { type: "number"; step: number; suffix?: string }
  | { type: "days" }
  | { type: "amount" }
  | { type: "streak" }
  | { type: "weekly" }
  | { type: "struggling" };

interface PolicyMeta {
  label: string;
  /** What changes when the value is set: shown to the supervisor before saving (docs/14, requirement 9). */
  effect: string;
  input: PolicyInput;
}

export const POLICY_META: Record<PolicyKey, PolicyMeta> = {
  "memorization.unit": {
    label: "وحدة الحفظ",
    effect: "الوحدة التي يُقاس بها المقدار اليومي في الخطة. الوجه هو الصفحة في مصحف المدينة.",
    input: { type: "select", options: unitLabel },
  },
  "memorization.direction": {
    label: "اتجاه الحفظ",
    effect: "الترتيب الافتراضي للخطة الجديدة. يمكن للمشرف تغييره لكل طالب عند إنشاء خطته.",
    input: { type: "select", options: { backward: "من الناس إلى البقرة", forward: "من البقرة إلى الناس" } },
  },
  "memorization.dailyAmount": {
    label: "المقدار اليومي للحفظ",
    effect: "مقدار الحفظ الجديد في كل يوم دراسي، بوحدة الحفظ.",
    input: { type: "number", step: 0.25 },
  },
  "memorization.minDailyAmount": {
    label: "الحد الأدنى للمقدار اليومي",
    effect: "لا تُنشأ خطة أو تعديل يقل مقداره اليومي عن هذا الحد.",
    input: { type: "number", step: 0.25 },
  },
  "memorization.maxDailyAmount": {
    label: "الحد الأعلى للمقدار اليومي",
    effect: "لا تتجاوزه أي زيادة يدوية أو تلقائية في المقدار.",
    input: { type: "number", step: 0.25 },
  },
  "schedule.studyDays": {
    label: "أيام الدراسة",
    effect: "الأيام التي تُولَّد فيها عناصر الخطة. أيام الإجازة لا يُطلب فيها حفظ ولا مراجعة.",
    input: { type: "days" },
  },
  "review.near": {
    label: "المراجعة القريبة",
    effect: "مقدار يُراجَع يوميًا من آخر ما حفظه الطالب.",
    input: { type: "amount" },
  },
  "review.far": {
    label: "المراجعة البعيدة",
    effect: "مقدار يومي يدور على كامل المحفوظ حتى يُغطّى كله ثم يبدأ من جديد.",
    input: { type: "amount" },
  },
  "taahud.streakIncrease": {
    label: "زيادة المقدار بعد أيام ناجحة متتالية",
    effect: "في برنامج التعاهد فقط: يزيد المقدار اليومي بعد عدد الأيام الناجحة المتتالية المحدد.",
    input: { type: "streak" },
  },
  "taahud.weeklyReview": {
    label: "المراجعة الشاملة الأسبوعية",
    effect: "في برنامج التعاهد فقط: يوم في الأسبوع للمراجعة الشاملة بدل الحفظ الجديد.",
    input: { type: "weekly" },
  },
  "grading.maxScore": {
    label: "الدرجة النهائية",
    effect: "الدرجة التي تُحسب منها نتيجة التسميع. لا تؤثر على النتائج المسجلة سابقًا.",
    input: { type: "number", step: 1 },
  },
  "grading.passScore": {
    label: "درجة النجاح اليومية",
    effect: "النتيجة الأقل منها تُعدّ غير ناجحة وتطبق عليها سياسة إعادة التسميع.",
    input: { type: "number", step: 0.5 },
  },
  "attempts.max": {
    label: "عدد محاولات التسميع",
    effect: "عدد المحاولات المسموحة للمقدار نفسه قبل تطبيق الإجراء عند الاستنفاد.",
    input: { type: "number", step: 1 },
  },
  "attempts.onExhausted": {
    label: "الإجراء عند استنفاد المحاولات",
    effect: "ما يحدث بعد آخر محاولة غير ناجحة.",
    input: {
      type: "select",
      options: { repeat_amount: "إعادة المقدار في اليوم التالي", refer_supervisor: "إحالة للمشرف" },
    },
  },
  "absence.policy": {
    label: "سياسة الغياب",
    effect: "ماذا يحدث لمقدار يوم الغياب في الخطة.",
    input: {
      type: "select",
      options: {
        shift: "تأجيل الخطة لليوم الدراسي التالي",
        carry_over: "ترحيل المقدار غير المنجز",
        skip: "تجاوز المقدار الفائت",
        pause: "إيقاف الخطة مؤقتًا",
      },
    },
  },
  "absence.carryOverAlert": {
    label: "حد التنبيه لتراكم المقدار المرحّل",
    effect: "عند تجاوز المقدار المتراكم هذا الحد (بوحدة الحفظ) يُنبَّه المعلم والمشرف.",
    input: { type: "number", step: 0.5 },
  },
  "struggling.rules": {
    label: "مؤشرات الطالب المتعثر",
    effect:
      "يُنبَّه المعلم والمشرف بالطالب الذي يبلغ عدد المؤشرات المطلوب خلال الفترة، مع الأسباب. المؤشر الفارغ لا يُحسب.",
    input: { type: "struggling" },
  },
  "results.editWindowHours": {
    label: "مدة تعديل النتيجة (بالساعات)",
    effect: "المدة التي يعدّل خلالها المعلم نتيجة سجلها، مع حفظ النتيجة الأصلية وسبب التعديل.",
    input: { type: "number", step: 1, suffix: "ساعة" },
  },
};

/** Human-readable value. */
export function formatPolicyValue(key: PolicyKey, value: unknown): string {
  const input = POLICY_META[key].input;
  switch (input.type) {
    case "select":
      return input.options[value as string] ?? String(value);
    case "number":
      return `${value}${input.suffix ? ` ${input.suffix}` : ""}`;
    case "days":
      return (value as Weekday[]).map((d) => weekdayLabel[d]).join("، ");
    case "amount": {
      const a = value as { amount: number; unit: AmountUnit };
      return `${a.amount} × ${unitLabel[a.unit]}`;
    }
    case "streak": {
      const s = value as { enabled: boolean; days: number; increment: number };
      return s.enabled ? `بعد ${s.days} أيام ناجحة يزيد المقدار ${s.increment}` : "معطّل";
    }
    case "weekly": {
      const s = value as { enabled: boolean; day: Weekday };
      return s.enabled ? `كل ${weekdayLabel[s.day]}` : "معطّل";
    }
    case "struggling": {
      const r = value as StrugglingValue;
      const parts = STRUGGLING_FIELDS.filter((f) => r[f.key] !== null).map((f) => `${f.short} ${r[f.key]}`);
      return `خلال ${r.periodDays} يومًا، ${r.minIndicators} من: ${parts.join("، ")}`;
    }
  }
}

export interface StrugglingValue {
  periodDays: number;
  minIndicators: number;
  repeats: number | null;
  unfinished: number | null;
  absences: number | null;
  carried: number | null;
}
export const STRUGGLING_FIELDS = [
  { key: "repeats", label: "عدد مرات «إعادة» في التسميع", short: "إعادة ≥" },
  { key: "unfinished", label: "مقادير حفظ فات يومها ولم تُنجز", short: "غير منجز ≥" },
  { key: "absences", label: "أيام الغياب", short: "غياب ≥" },
  { key: "carried", label: "المقدار المرحّل (بوحدة الحفظ)", short: "مرحّل ≥" },
] as const;
export const strugglingIndicatorLabel = {
  repeats: "تكرار «إعادة»",
  unfinished: "مقادير غير منجزة",
  absences: "تكرار الغياب",
  carried: "تراكم المرحّل",
} as const;

/** A starting value for the edit form when the key is unset everywhere. */
export function blankPolicyValue(key: PolicyKey): unknown {
  const input = POLICY_META[key].input;
  switch (input.type) {
    case "select":
      return Object.keys(input.options)[0];
    case "number":
      return 1;
    case "days":
      return ["sun", "mon", "tue", "wed", "thu"];
    case "amount":
      return { amount: 1, unit: "page" };
    case "streak":
      return { enabled: true, days: 5, increment: 0.5 };
    case "weekly":
      return { enabled: true, day: "sat" };
    case "struggling":
      return { periodDays: 14, minIndicators: 2, repeats: 3, unfinished: 3, absences: 3, carried: null };
  }
}
